import React from 'react';
import { Box, Container, Typography, Paper, useTheme, useMediaQuery, Link } from '@mui/material';
import { motion } from 'framer-motion';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LanguageIcon from '@mui/icons-material/Language';

const Contact = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const contactInfo = [
    {
      icon: <PhoneIcon />,
      label: 'Phone',
      value: '+91 9831797749',
      link: 'tel:+919831797749',
      secondary: '+234 9023395567',
      secondaryLink: 'tel:+2349023395567',
      gradient: 'linear-gradient(90deg, #0968E5, #091970)',
    },
    {
      icon: <EmailIcon />,
      label: 'Email',
      value: 'Ajay.jhunjhunwala7@gmail.com',
      link: 'mailto:Ajay.jhunjhunwala7@gmail.com',
      gradient: 'linear-gradient(90deg, #0968E5, #091970)',
    },
    {
      icon: <LanguageIcon />,
      label: 'Website',
      value: 'ajayjhunjhunwala.com',
      link: 'https://ajayjhunjhunwala.com',
      isExternal: true,
      gradient: 'linear-gradient(90deg, #0968E5, #091970)',
    },
    {
      icon: <LocationOnIcon />,
      label: 'Location',
      value: 'Kolkata, India',
      gradient: 'linear-gradient(90deg, #0968E5, #091970)',
    },
    {
      icon: <LinkedInIcon />,
      label: 'LinkedIn',
      value: 'Connect',
      link: 'https://www.linkedin.com/in/ajay-jhunjhunwala-30106a21/',
      isExternal: true,
      gradient: 'linear-gradient(90deg, #0968E5, #091970)',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.6 },
    },
  };

  return (
    <Box
      sx={{
        py: { xs: 6, md: 8 },
        background: 'linear-gradient(90deg, #0968E5, #091970)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Container maxWidth="md" sx={{ width: '100%' }}>
        <Typography
          variant={isMobile ? 'h5' : 'h4'}
          sx={{
            textAlign: 'center',
            mb: { xs: 3, md: 4 },
            fontWeight: 700,
            color: 'white',
            textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
          }}
        >
          Let's Connect
        </Typography>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1.5,
              justifyContent: 'center',
              mb: 2,
            }}
          >
            {contactInfo.map((info, index) => (
              <Box
                key={index}
                sx={{
                  flex: { xs: '0 0 calc(50% - 6px)', sm: '0 0 calc(33.333% - 10px)', md: '0 0 calc(20% - 12px)' },
                  maxWidth: { xs: 'calc(50% - 6px)', sm: 'calc(33.333% - 10px)', md: 'calc(20% - 12px)' },
                  minWidth: { xs: 'calc(50% - 6px)', sm: 'calc(33.333% - 10px)', md: 'calc(20% - 12px)' },
                }}
              >
                <motion.div variants={itemVariants}>
                  <Paper
                    component={info.link ? Link : Box}
                    href={info.link}
                    target={info.isExternal ? '_blank' : undefined}
                    rel={info.isExternal ? 'noopener noreferrer' : undefined}
                    sx={{
                      p: { xs: 1.5, sm: 2 },
                      borderRadius: 2,
                      background: 'rgba(255, 255, 255, 0.95)',
                      backdropFilter: 'blur(10px)',
                      textAlign: 'center',
                      textDecoration: 'none',
                      color: 'inherit',
                      transition: 'all 0.3s ease',
                      cursor: info.link ? 'pointer' : 'default',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      height: '100%',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        boxShadow: '0 8px 20px rgba(9, 104, 229, 0.3)',
                        border: `1px solid ${info.gradient.includes('0968E5') ? '#0968E5' : '#091970'}`,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: 36, sm: 40 },
                        height: { xs: 36, sm: 40 },
                        borderRadius: '50%',
                        background: info.gradient,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        mb: 1,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        '& svg': {
                          fontSize: { xs: 18, sm: 20 },
                        },
                      }}
                    >
                      {info.icon}
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: '#666',
                        mb: 0.25,
                        fontSize: { xs: '0.65rem', sm: '0.7rem' },
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      {info.label}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 500,
                        color: '#333',
                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                        mb: info.secondary ? 0.25 : 0,
                        wordBreak: 'break-word',
                        lineHeight: 1.3,
                      }}
                    >
                      {info.value}
                    </Typography>
                    {info.secondary && (
                      <Typography
                        variant="caption"
                        component={info.secondaryLink ? Link : 'span'}
                        href={info.secondaryLink}
                        sx={{
                          fontWeight: 500,
                          color: '#0968E5',
                          fontSize: { xs: '0.7rem', sm: '0.75rem' },
                          textDecoration: 'none',
                          lineHeight: 1.3,
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        {info.secondary}
                      </Typography>
                    )}
                  </Paper>
                </motion.div>
              </Box>
            ))}
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Contact;

